import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { UserRole } from '@data-access/roles';
import { submissionDetailTokenizer } from '@data-access/submissions/store';
import {
  BasePermission,
  PermissionGrants,
  PermissionNames,
  QuestionnaireAccess,
  ReassignSubmission,
  Role,
  SiteCorrespondence,
  SiteSubmission,
  StudyCorrespondence,
  StudySubmission,
  SubmissionDocumentAccess,
  SubmissionSuperAdmin,
  UpdateSubmissionPriority,
  UpdateSubmissionTeam,
} from '@shared/interfaces/permissions.interface';
import { convertKeysToCamelCase, convertSnakeToCamelCase } from '@shared/utils';
import { AuthService } from '@auth/auth.service';
import {
  ResubmitPayload,
  SubmissionResponse,
  UpdateSubmissionStagePayload,
} from '../types';
import environment from '@environments/environment';
import { PermissionsService } from '@shared/services/permissions.service';
import { combineLatest, map } from 'rxjs';
import {
  DIALOG_TYPE,
  OpenDialogState,
  WcgSingleTextDialogComponent,
} from '@data-access/shared';
import { DIALOG_MODES } from '@erm/cluster';
import { DialogSize } from '@wcg/frontend-framework';
import { Validators } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { SubmissionSite } from '@data-access/sites';

const EXTERNAL_EDIT_REMOVE_EXCLUDED_PERMISSIONS = new Set<PermissionNames>([
  // This is not applicable inside a submission.
  PermissionNames.NewSiteSubmission,
  // This is being removed soon from backend
  PermissionNames.AppealSubmission,
]);

@Injectable()
export class SubmissionDetailFacadeService {
  /** Injected Services */
  private readonly _submissionDetailStore = submissionDetailTokenizer();
  private readonly _authService = inject(AuthService);
  private readonly _permissionsService = inject(PermissionsService);
  private readonly _destroyRef = inject(DestroyRef);
  /** Injected Services */

  /** Signals */
  /** The ID of the current submission */
  $submissionId = this._submissionDetailStore.$submissionId;
  /** The current stage of the submission */
  $currentStage = this._submissionDetailStore.$current_stage;
  /** The current submission */
  submission = this._submissionDetailStore.$submission;

  readonly $submission = signal<SubmissionResponse | null>(null);
  /** Loading state of the submission */
  $isLoading = this._submissionDetailStore.$isLoading;
  $error = this._submissionDetailStore.$error;
  readonly $isInternal = toSignal(this._permissionsService.isInternal$);
  readonly $user$ = toSignal(this._authService.user$);

  $hasOutcomeDocuments = this._submissionDetailStore.$hasOutcomeDocuments;
  $sites = this._submissionDetailStore.$sites;
  $organiationStatus = this._submissionDetailStore.$organiationStatus;
  $reloadContactOrgs = this._submissionDetailStore.$reloadContactOrgs;

  $organizationValidationAssignedTypes =
    this._submissionDetailStore.$organizationValidationAssignedTypes;
  $organizationValidationUnassignedTypes =
    this._submissionDetailStore.$organizationValidationUnassignedTypes;

  // $sites = this._submissionDetailStore.$sites;

  loadSites(submissionId: string, queryParams?: HttpParams) {
    this._submissionDetailStore.loadSubmissionSites(submissionId, queryParams);
  }

  private readonly _canRemoveSignal = signal<boolean | null>(null);
  readonly $showTasksDetails = signal<boolean>(environment.enabledTask);
  /** Signals */

  /** Computed Signals */
  readonly $canEdit = computed(() =>
    this._getSubmissionPermissions(
      [SiteSubmission, StudySubmission],
      [PermissionGrants.CanEdit],
    ),
  );
  readonly $canRemove = computed(() => {
    const submission = this.$submission();
    if (!submission) {
      return null;
    }
    return this._canRemoveSignal();
  });

  readonly $canReassign = computed(() =>
    this._getSubmissionPermissions(
      [ReassignSubmission],
      [PermissionGrants.CanEdit],
    ),
  );

  readonly $canViewSubmissionSites = computed(() =>
    this._getSubmissionPermissions(
      [SiteSubmission, StudySubmission],
      [PermissionGrants.CanView],
    ),
  );

  readonly $canEditSubmissionDetails = computed(() =>
    this._getSubmissionPermissions(
      [QuestionnaireAccess],
      [PermissionGrants.CanEdit],
    ),
  );

  readonly $canViewSubmissionDocuments = computed(() =>
    this._getSubmissionPermissions(
      [SubmissionDocumentAccess],
      [PermissionGrants.CanView],
    ),
  );

  readonly $canUpdateSubmissionPriority = computed(() =>
    this._getSubmissionPermissions(
      [UpdateSubmissionPriority],
      [PermissionGrants.CanEdit],
    ),
  );

  readonly $canUpdateSubmissionTeam = computed(() =>
    this._getSubmissionPermissions(
      [UpdateSubmissionTeam],
      [PermissionGrants.CanEdit],
    ),
  );

  /** ERM-11601 — internal correspondence grant, from the user's global role (submission.role is trimmed for internal viewers). */
  readonly $internalCanEditCorrespondence = toSignal(
    this._permissionsService.observeUserPermissions(
      [StudyCorrespondence, SiteCorrespondence],
      [PermissionGrants.CanEdit],
    ),
    { initialValue: false },
  );

  readonly $internalCanViewCorrespondence = toSignal(
    this._permissionsService.observeUserPermissions(
      [StudyCorrespondence, SiteCorrespondence],
      [PermissionGrants.CanView],
    ),
    { initialValue: false },
  );

  /** ERM-11601 — external Study/Site Correspondence edit grant; says nothing for internal viewers. */
  readonly $canEditCorrespondence = computed(() =>
    this._hasNamedSubmissionPermission(
      [PermissionNames.StudyCorrespondence, PermissionNames.SiteCorrespondence],
      PermissionGrants.CanEdit,
    ),
  );

  readonly $canEditSubmissionCorrespondence = computed(() =>
    this._hasNamedSubmissionPermission(
      [PermissionNames.SubmissionCorrespondence],
      PermissionGrants.CanEdit,
    ),
  );

  /** ERM-14364 — external Study/Site Correspondence view grant; says nothing for internal viewers. */
  readonly $canViewCorrespondence = computed(() =>
    this._hasNamedSubmissionPermission(
      [PermissionNames.StudyCorrespondence, PermissionNames.SiteCorrespondence],
      PermissionGrants.CanView,
    ),
  );

  readonly $canViewSubmissionCorrespondence = computed(() =>
    this._hasNamedSubmissionPermission(
      [PermissionNames.SubmissionCorrespondence],
      PermissionGrants.CanView,
    ),
  );

  readonly $isQC = computed(() => this._isUserSMEQC('qc'));
  readonly $isSME = computed(() => this._isUserSMEQC('sme'));

  /**
   * ERM-12196 — the name of the role the viewer's access to this submission
   * comes through. For an external user the v2 response carries their site or
   * study contact role verbatim, so this is the granted role name.
   */
  private readonly _$submissionRoleName = computed<string>(
    () => this.$submission()?.role?.name?.trim() ?? '',
  );

  /**
   * ERM-12196 — read-only external visibility grants. Both names are seeded
   * external-only roles, so the name alone identifies the grant; no
   * is_internal check is needed and none is wanted, because that arrives
   * asynchronously and would flip the gate after the drawer has rendered.
   */
  readonly $isStudyVisibilityGrant = computed(
    () => this._$submissionRoleName() === Role.StudyReadOnly,
  );
  readonly $isSiteVisibilityGrant = computed(
    () => this._$submissionRoleName() === Role.SiteVisibility,
  );
  readonly $isReadOnlyVisibilityGrant = computed(
    () => this.$isStudyVisibilityGrant() || this.$isSiteVisibilityGrant(),
  );

  /**
   * ERM-12647 — the one "has the external user resubmitted" signal every
   * consumer reads (To-do tab's post-resubmit confirmation, the header's
   * locked "Edits Submitted" badge). Backed by the server's `resubmitted_at`
   * stamp, NOT the open-to-do count: individual Mark done / answer
   * auto-complete (ERM-12595) both drop the open count to zero without a
   * resubmit, so counting can't tell "worked through their to-dos" apart
   * from "resubmitted". Reload-safe and cannot be undone by clearing the
   * browser, since it comes off the submission.
   */
  readonly $hasResubmitted = computed<boolean>(
    () => !!this.$submission()?.resubmitted_at,
  );

  /**
   * ERM-13220 / ERM-12317 — the one "may this submission still be edited"
   * signal, read straight off the server's `open_for_edits`.
   *
   * The backend owns this state as a stored flag per axis
   * (`Workspace.questionnaire_open_for_edits` and
   * `documents_unlocked_as_whole`), set together when an internal user raises a
   * customer to-do and cleared together by whichever exit comes first: the
   * client's resubmit, or an internal user advancing to Review. So it already
   * means "open, and not yet resubmitted".
   *
   * Consumers must not re-derive this from the open-to-do count. That count
   * drops to zero mid-cycle — answering a question auto-resolves its to-do to
   * `ready_for_review` (ERM-12595) — while the submission is still open and
   * unresubmitted, and the backend still accepts the edit. Counting therefore
   * hides actions the API would allow.
   */
  readonly $openForEdits = computed<boolean>(
    () => !!this.$submission()?.open_for_edits,
  );

  /** Computed Signals */

  constructor() {
    // Watch for submission changes and update canRemove signal using observables
    toObservable(this.$submission)
      .pipe(takeUntilDestroyed())
      .subscribe((submission) => {
        if (submission) {
          this._updateCanRemoveSignal(submission);
        }
      });

    toObservable(this.submission)
      .pipe(takeUntilDestroyed())
      .subscribe((submission) => {
        if (submission?.id) {
          this.$submission.set(submission);
        }
      });
  }

  getSubmissionDetail(submissionId: string) {
    return this._submissionDetailStore.loadSubmission(submissionId);
  }

  /**
   * ERM-12647 — the external user's explicit Re-submit. The store patches the
   * returned submission, so `resubmitted_at` surfaces without a second fetch.
   */
  resubmit(workspaceId: string, payload: ResubmitPayload) {
    this._submissionDetailStore.resubmit(workspaceId, payload);
  }

  /** ERM-13220 / ERM-14181 — see the store; guard lives there. */
  markSubmissionOpenForEdits(expectedSubmissionId?: string) {
    this._submissionDetailStore.markSubmissionOpenForEdits(
      expectedSubmissionId,
    );
  }

  updateSubmission(submission: SubmissionResponse) {
    this._submissionDetailStore.updateSubmission(submission);
    this._updateCanRemoveSignal(submission);
  }

  resetSubmission() {
    this._submissionDetailStore.resetState();
  }
  /**
   * Delete a submission.
   * @param workspaceId - The ID of the workspace to delete.
   * @param reason_for_delete - The reason for deleting the submission.
   * @param isInternal - Whether the user is internal.
   * @returns An Observable that completes when the deletion is done.
   */
  deleteSubmission(
    workspaceId: string,
    reason_for_delete: string,
    isInternal: boolean,
  ) {
    return this._submissionDetailStore.deleteSubmission(
      workspaceId,
      reason_for_delete,
      isInternal,
    );
  }

  /*
   * A private method to check if the user has the required permissions for a submission.
   * @param permissions - An array of BasePermission types to check against.
   * @param grants - An array of PermissionGrants that the user must have.
   * @return A boolean indicating if the user has all the required permissions, or null if no submission is found.
   */
  private _getSubmissionPermissions(
    permissions: BasePermission[],
    grants: PermissionGrants[],
  ) {
    const submission = this.$submission();
    if (!submission) {
      return null;
    }
    const submissionRole: UserRole = convertKeysToCamelCase(
      submission.role,
    ) as UserRole;
    const rolePermissions = this._filterExcludedPermissions(
      submissionRole?.permissions,
      grants,
    );

    // Check if the user has all the required grants
    return grants.every((userGrant: PermissionGrants) => {
      const formattedUserGrant = convertSnakeToCamelCase(userGrant);
      // Check if the formatted permission exists in the role's permission,
      // and returns true if found
      if (rolePermissions) {
        return rolePermissions.some(
          (perm) => perm[formattedUserGrant as keyof typeof perm],
        );
      }
      // If no permissions are found, return false
      return false;
    });
  }

  /*
   * A private method to check if the submission role carries one of the named
   * permissions with the given grant.
   *
   * Unlike `_getSubmissionPermissions`, this matches on the permission name,
   * so a caller that needs one specific permission gets the answer it asked
   * for rather than "any permission with this grant".
   *
   * @param names - The permission names to look for.
   * @param grant - The grant that permission has to carry.
   * @return A boolean indicating whether the role has one of them granted.
   */
  private _hasNamedSubmissionPermission(
    names: PermissionNames[],
    grant: PermissionGrants,
  ): boolean {
    const submission = this.$submission();
    if (!submission) {
      return false;
    }
    const submissionRole: UserRole = convertKeysToCamelCase(
      submission.role,
    ) as UserRole;
    // The keys are camelCased above, so `can_edit` reads back as `canEdit`.
    const grantKey = convertSnakeToCamelCase(grant);
    return (submissionRole?.permissions ?? []).some(
      (permission) =>
        names.includes(permission.name as PermissionNames) &&
        !!permission[grantKey as keyof typeof permission],
    );
  }

  private _filterExcludedPermissions<T extends { name?: string }>(
    permissions: T[] | undefined,
    grants: PermissionGrants[],
  ): T[] | undefined {
    if (!permissions || this.$isInternal()) {
      return permissions;
    }

    const isEditOrRemove = grants.some(
      (grant) =>
        grant === PermissionGrants.CanEdit ||
        grant === PermissionGrants.CanRemove,
    );
    if (!isEditOrRemove) {
      return permissions;
    }

    return permissions.filter(
      (permission) =>
        !EXTERNAL_EDIT_REMOVE_EXCLUDED_PERMISSIONS.has(
          permission.name as PermissionNames,
        ),
    );
  }

  /**
   * A private method to check if the current user is in a specific role (qc or sme) for the submission.
   * @param role - The role to check ('qc' or 'sme').
   * @return A boolean indicating if the user is an SME or QC for the submission
   */
  private _isUserSMEQC(role: 'qc' | 'sme'): boolean {
    const submission = this.$submission();
    const user = this.$user$();

    if (!submission) {
      return false;
    }
    if (!user) {
      return false;
    }

    return submission[role]?.id === user.id;
  }

  /*
   * * A method to update the submission stage.
   * @param workspaceID - The ID of the workspace associated with the submission.
   */
  updateSubmissionStage(payload: UpdateSubmissionStagePayload) {
    this._submissionDetailStore.updateSubmissionStage(payload);
  }

  organizationValidation(workspaceID: string) {
    this._submissionDetailStore.organizationValidation(workspaceID);
  }

  /*
   * A method to complete an IBC submission.
   * @param workspaceID - The ID of the workspace to complete the IBC submission for.
   */
  completeIbcSubmission(workspaceID: string) {
    this._submissionDetailStore.completeIbcSubmission(workspaceID);
  }

  /*
   * A method to open a dialog for updating the submission name.
   * @param submissionName - The current name of the submission to be edited.
   */
  updateSubmssionName(submissionName: string): void {
    const dialogPayload: OpenDialogState = {
      component: () => WcgSingleTextDialogComponent,
      componentData: {
        data: submissionName,
        dialogType: DIALOG_TYPE.SingleText,
        errorText: 'Submission Name is required',
        isRequired: true,
        label: 'Submission Name',
        mode: DIALOG_MODES.EDITNAME,
        propKey: 'name',
        size: DialogSize.MEDIUM,
        title: 'Edit Submission Name',
        validatorFn: () => Validators.required,
      },
      type: DIALOG_TYPE.SingleText,
    };

    this._editNameDialog(dialogPayload);
  }

  /**
   * Private method to handle the edit name dialog logic
   * @param payload - The dialog configuration payload
   */
  private _editNameDialog(payload: OpenDialogState): void {
    const dialogRef = this._submissionDetailStore.openDialog(payload);
    if (dialogRef) {
      dialogRef
        .afterClosed()
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          error: (err: Error) => {
            console.error('Error handling dialog result:', err);
          },
          next: (data: { value: string; mode: string; index?: number }) => {
            this._updateSubmissionName(data.value);
          },
        });
    }
  }

  /**
   * Private method to update the submission name via signal store
   * @param value - The new name value
   */
  private _updateSubmissionName(value: string): void {
    const submission = this.$submission();
    if (!submission?.id) {
      console.error('No submission ID available for name update');
      return;
    }

    // Use the signal store method to handle the API call and state update
    this._submissionDetailStore.updateSubmissionName(submission.id, value);
  }

  private _updateCanRemoveSignal(submission: SubmissionResponse) {
    const canRemove = combineLatest([
      // Check remove permission for the submission
      this._permissionsService.observeEntityPermissions(
        submission.id,
        [StudySubmission, SiteSubmission],
        [PermissionGrants.CanRemove],
      ),
      // Check user permissions
      this._permissionsService.observeUserPermissions(
        [SubmissionSuperAdmin],
        [PermissionGrants.CanRemove],
      ),

      // Check if the user is internal
      this._permissionsService.isInternal$,
    ]).pipe(
      map(([removePermission, userPermissions, isInternal]) => {
        // IF the user has submission super admin permission, they can always delete the submission
        if (userPermissions) {
          return true;
        }

        // If the submission is in draft stage and the user has edit or remove permission, they can delete the submission
        if (submission.current_stage === 'draft' && removePermission) {
          return true;
        }

        const rolePermissions = this._filterExcludedPermissions(
          submission.role?.permissions,
          [PermissionGrants.CanRemove],
        );
        const grantedPermissions = rolePermissions?.some((p) => {
          const permission = p as {
            can_remove?: boolean;
            canRemove?: boolean;
          };
          const canRemove =
            'can_remove' in permission
              ? permission.can_remove
              : permission.canRemove;
          return canRemove;
        });

        if (submission.current_stage === 'draft' && grantedPermissions) {
          return true;
        }

        // If the submission is in qc_check stage and the user is internal with remove permission, they can delete the submission
        if (
          submission.current_stage === 'qc_check' &&
          isInternal &&
          (removePermission || userPermissions)
        ) {
          return true;
        }

        return false;
      }),
    );

    canRemove
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((canRemoveValue: boolean) =>
        this._canRemoveSignal.set(canRemoveValue),
      );
  }

  updateSubmissionSites(data: SubmissionSite[], total: number) {
    this._submissionDetailStore.updateSubmissionSites(data, total);
  }

  /**
   * Trigger loading the appeal submission setup path.
   * Path is set in $appealSubmissionSetupPath; caller should navigate then call clearAppealSubmissionSetupPath.
   * @param submissionId - The ID of the submission to create an appeal for
   */
  openAppealSubmissionSetup(submissionId: string) {
    this._submissionDetailStore.openAppealSubmissionSetup(submissionId);
  }

  /** Clear the appeal submission setup path after navigation */
  clearAppealSubmissionSetupPath() {
    this._submissionDetailStore.clearAppealSubmissionSetupPath();
  }

  /** Path to navigate for appeal submission setup (set by openAppealSubmissionSetup) */
  get $appealSubmissionSetupPath() {
    return this._submissionDetailStore.$appealSubmissionSetupPath;
  }

  markSubmissionReSubmitted(expectedSubmissionId?: string) {
    const submission = this._submissionDetailStore.$submission();
    if (submission && submission.id === expectedSubmissionId) {
      this._submissionDetailStore.updateSubmission({
        ...submission,
        open_for_edits: false,
        resubmitted_at: new Date().toISOString(),
      });
    }
  }
}
